import { Ingredient, IngredientGroup } from '@/types';
import { UI } from '@/Components/UI';
import { IngredientsList } from '@/Components/Specific/Recipe/Show/IngredientsList';
import { useMemo } from 'react';
import { useConfig } from '@/hooks';
import { calculateBaseAmount } from '@/utils';

type IngredientGroupsListProps = {
    ingredientGroups: IngredientGroup[];
    initialServings: number;
    servings: number;
    isSummarized?: boolean;
};

export const IngredientGroupsList = ({
                                         ingredientGroups,
                                         initialServings,
                                         servings,
                                         isSummarized
                                     }: IngredientGroupsListProps) => {
    const { measuringUnits } = useConfig();

    // Merge all ingredients from all groups into a single list and sum their amounts
    const summarizedIngredients: Ingredient[] = useMemo(() => {
        const ingredientMap: Record<string, Ingredient> = {};

        ingredientGroups.forEach((group) => {
            group.ingredients.forEach((ingredient) => {
                const { amount, unitInfo } = calculateBaseAmount(ingredient.amount, ingredient.measuringUnit, measuringUnits);

                const key = `${ingredient.food.id}-${unitInfo?.unit ?? ingredient.measuringUnit}`;

                if (!ingredientMap[key]) {
                    ingredientMap[key] = { ...ingredient, measuringUnit: unitInfo?.unit ?? ingredient.measuringUnit, amount: 0 };
                }

                ingredientMap[key].amount += amount;
            });
        });

        // Sort ingredients by food name
        return Object
            .values(ingredientMap)
            .sort((a, b) => {
                return a.food.name.localeCompare(b.food.name, undefined, { sensitivity: 'base' });
            });
    }, [ingredientGroups, measuringUnits]);

    return (
        <UI.Card>
            {isSummarized ? (
                <IngredientsList
                    ingredients={summarizedIngredients}
                    initialServings={initialServings}
                    servings={servings} />
            ) : (
                <div className={'flex flex-col gap-2'}>
                    {ingredientGroups.map((group, index) => (
                        <div key={index}>
                            {group.title && (
                                <UI.Heading
                                    level={3}
                                    className={'mt-0'}>
                                    {group.title}
                                </UI.Heading>
                            )}

                            <IngredientsList
                                ingredients={group.ingredients}
                                initialServings={initialServings}
                                servings={servings} />
                        </div>
                    ))}
                </div>
            )}
        </UI.Card>
    );
};
